import type { MediaOrigin, PublicMedia } from './media.js'

/**
 * Le préfixe sous lequel les images sont servies (voir `app.ts`, où l'API les
 * monte, et deploy/README.md pour le proxy qui l'atteint).
 * Partagé plutôt que recopié dans le site, l'administration et le design
 * system : trois copies de cette convention finiraient par diverger, et le
 * symptôme serait une image manquante, pas une erreur de compilation.
 */
export const MEDIA_BASE_PATH = '/media'

export function mediaUrl(id: string, width: number): string {
  return `${MEDIA_BASE_PATH}/${id}/${width}.webp`
}

/** Le `srcset` d'un média, construit sur les seules largeurs réellement produites. */
export function mediaSrcSet(media: Pick<PublicMedia, 'id' | 'widths'>): string {
  return media.widths.map((largeur) => `${mediaUrl(media.id, largeur)} ${largeur}w`).join(', ')
}

/** Le point focal, traduit en `object-position` : ce que le cadrage préserve. */
export function mediaObjectPosition(media: Pick<PublicMedia, 'focalX' | 'focalY'>): string {
  return `${media.focalX}% ${media.focalY}%`
}

/**
 * Le signalement dû pour chaque origine, au sens de l'article 50. `null` n'est
 * pas un oubli : une photographie authentique ne se signale pas.
 */
export const AI_DISCLOSURE_LABELS: Record<MediaOrigin, string | null> = {
  photo: null,
  'ia-generee': 'Image générée par IA',
  'ia-retouchee': 'Image retouchée par IA',
}

export function requiresAiDisclosure(origin: MediaOrigin): boolean {
  return AI_DISCLOSURE_LABELS[origin] !== null
}

/**
 * Ce que la gérante lit dans le sélecteur d'origine. Partagé entre le champ
 * visuel et la bibliothèque : une même origine ne peut pas s'appeler
 * autrement selon l'écran où on la déclare.
 */
export const MEDIA_ORIGIN_LABELS: Record<MediaOrigin, string> = {
  photo: 'Photographie authentique',
  'ia-generee': 'Visuel généré par IA',
  'ia-retouchee': 'Photo retouchée par IA',
}

/**
 * La description d'un visuel, mention de signalement comprise. Le badge se voit,
 * cette mention s'entend : sans elle, une personne qui navigue au lecteur
 * d'écran serait la seule à ne pas savoir qu'un visuel a été produit par IA.
 */
export function mediaAlt(media: Pick<PublicMedia, 'alt' | 'origin'>): string {
  const description = media.alt.trim()
  const mention = AI_DISCLOSURE_LABELS[media.origin]
  if (!mention) return description
  const ponctuee = /[.!?…]$/.test(description) ? description : `${description}.`
  return `${ponctuee} ${mention}.`
}

/**
 * Tout ce dont `MediaImage` a besoin, en un seul appel — et c'est bien le
 * point : `disclosure` sort d'ici avec le reste, plutôt que d'être une
 * décision prise à chaque emplacement. Un intégrateur ne peut pas « oublier »
 * un signalement qu'il n'a jamais eu à demander.
 *
 * Le design system ne dépend pas de ce paquet (son lot doit rester autonome
 * pour Claude Design) : c'est donc l'appelant qui compose, mais il compose
 * d'un seul geste.
 */
export function mediaImageProps(media: PublicMedia, sizes?: string) {
  return {
    // La plus grande largeur produite en `src` : le repli des navigateurs qui
    // ignorent `srcset`, et jamais une image plus petite que demandée.
    src: mediaUrl(media.id, media.widths[media.widths.length - 1]),
    srcSet: mediaSrcSet(media),
    sizes,
    alt: mediaAlt(media),
    objectPosition: mediaObjectPosition(media),
    disclosure: AI_DISCLOSURE_LABELS[media.origin],
  }
}
